/**
 * The tutorial run's session hooks: TutorialDirector (step logic) + TutorialOverlay (prompts) +
 * RotationTracker (graduation score), wired to the session through tutorial/hooks.ts.
 *  - locked binds and Dodge ignore presses until their lesson unlocks them;
 *  - bots are held / tank-only / free per step, and their kicks are held while you learn to kick;
 *  - every tick feeds the director the sim events; every frame redraws the overlay.
 */
import type { ContentRegistry, WorldShape } from '@keystone/sim';
import { TUTORIAL } from '@keystone/content';
import type { SessionHooks } from './hooks';
import { type Lock, TutorialDirector, type TutorialStats } from './director';
import { TutorialOverlay } from './overlay';
import { type RotationScore, RotationTracker } from './score';
import { buildScript } from './scripts';

export interface TutorialOutcome {
  classId: string;
  /** Every step done (the graduation pack died). */
  graduated: boolean;
  /** Graduation rotation score (null if the graduation pull never sampled). */
  score: RotationScore | null;
  stats: TutorialStats;
}

export interface TutorialRunHandle {
  hooks: SessionHooks;
  outcome(): TutorialOutcome;
  /** Debug / tests: the live director (null before the session attached). */
  director(): TutorialDirector | null;
}

export function tutorialRun(classId: string, content: ContentRegistry, o: { onSkip(): void }): TutorialRunHandle {
  const script = buildScript(classId, content, TUTORIAL.moveSpot);
  let director: TutorialDirector | null = null;
  let overlay: TutorialOverlay | null = null;
  let tracker: RotationTracker | null = null;
  let project: ((p: { x: number; y: number }, h?: number) => { x: number; y: number; visible: boolean }) | null = null;
  let selected: () => string | null = () => null;
  const all: Lock[] = [...script.allLocks, 'dodge'];

  const hooks: SessionHooks = {
    attach(api) {
      if (!api.playerId) return;
      const q = api.queries;
      director = new TutorialDirector(script, {
        playerId: api.playerId,
        layout: { packs: TUTORIAL.packs, moveSpot: TUTORIAL.moveSpot },
        queries: {
          isKick: (id) => !!api.content.ability(id)?.ui?.tags?.includes('interrupt'),
          isDispellable: (id) => {
            const t = api.content.aura(id)?.dispelType;
            return t === 'poison' || t === 'magic';
          },
          contains: (area, p, r) => {
            try {
              return q.spatial.shapeContains(area as WorldShape, p, r);
            } catch {
              return false;
            }
          },
        },
      });
      const d = director;
      // The graduation pull counts from the moment any of its enemies is in combat (standing far away is idle time too).
      const grad = TUTORIAL.packs.graduation;
      tracker = new RotationTracker(api.content, q, api.playerId, () => d.scoring(), (w, me) => me.inCombat || (w.packs[grad]?.memberIds ?? []).some((id) => !!w.units[id]?.alive && !!w.units[id]?.inCombat));
      overlay = new TutorialOverlay(api.hudHost, { onSkip: () => o.onSkip() });
      project = api.project;
      selected = api.selectedId;
      if (import.meta.env?.DEV) (globalThis as { __tutorial?: unknown }).__tutorial = { director: d, tracker };
    },
    allowBind: (b) => !director || director.isUnlocked(b),
    allowDodge: () => !director || director.isUnlocked('dodge'),
    filterBotCommands: (id, cmds) => (director ? cmds.filter((c) => director!.allowBotCommand(id, c as { type: string; abilityId?: string })) : cmds),
    onTick(events, world) {
      if (!director) return;
      director.update(events, world, selected());
      tracker?.step(events, world);
    },
    onFrame(t) {
      if (!director || !overlay || !project) return;
      const open = new Set(director.unlockedLocks());
      overlay.render({ view: director.view(), toasts: director.drainToasts(), locked: all.filter((l) => !open.has(l)), project, finished: director.finished }, t);
    },
    dispose() {
      overlay?.dispose();
      overlay = null;
    },
  };

  return {
    hooks,
    director: () => director,
    outcome: () => ({
      classId: script.classId,
      graduated: !!director?.finished,
      score: tracker && tracker.c.samples > 0 ? tracker.score() : null,
      stats: director?.stats ?? { kicks: 0, dispels: 0, dodges: 0, telegraphHits: 0, procsUsed: 0 },
    }),
  };
}
